// Utility functions for Brazilian formatting

/**
 * Formats a number as Brazilian Real currency
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

/**
 * Formats a number as Brazilian Real currency with custom options
 */
export const formatCurrencyCustom = (
  value: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2
  }).format(value);
};

/**
 * Parses a Brazilian currency string to number
 */
export const parseCurrency = (value: string): number => {
  // Remove currency symbol and convert to number
  const cleaned = value
    .replace(/[R$\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  return parseFloat(cleaned) || 0;
};

/**
 * Formats CNPJ with mask
 */
export const formatCNPJ = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
};

/**
 * Formats CPF with mask
 */
export const formatCPF = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return value;
};

/**
 * Formats phone number with Brazilian mask
 */
export const formatPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  if (cleaned.length <= 10) {
    // Landline: (11) 1234-5678
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else {
    // Mobile: (11) 91234-5678
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
};

/**
 * Formats CEP with mask
 */
export const formatCEP = (value: string): string => {
  const cleaned = value.replace(/\D/g, '').substring(0, 8);
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 5) return cleaned;
  return cleaned.replace(/(\d{5})(\d{1,3})/, '$1-$2');
};

/**
 * Validates CNPJ
 */
export const validateCNPJ = (cnpj: string): boolean => {
  cnpj = cnpj.replace(/\D/g, '');

  if (cnpj.length !== 14) return false;

  // Check for identical digits
  if (/^(\d)\1+$/.test(cnpj)) return false;

  // Validate check digits
  let sum = 0;
  let remainder;

  // First check digit
  for (let i = 1; i <= 12; i++) {
    sum += parseInt(cnpj.substring(i - 1, i)) * (i < 5 ? 6 - i : 14 - i);
  }

  remainder = sum % 11;
  if (remainder < 2) remainder = 0;
  else remainder = 11 - remainder;

  if (remainder !== parseInt(cnpj.substring(12, 13))) return false;

  // Second check digit
  sum = 0;
  for (let i = 1; i <= 13; i++) {
    sum += parseInt(cnpj.substring(i - 1, i)) * (i < 6 ? 7 - i : 15 - i);
  }

  remainder = sum % 11;
  if (remainder < 2) remainder = 0;
  else remainder = 11 - remainder;

  return remainder === parseInt(cnpj.substring(13, 14));
};

/**
 * Validates CPF
 */
export const validateCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/\D/g, '');

  if (cpf.length !== 11) return false;

  // Check for identical digits
  if (/^(\d)\1+$/.test(cpf)) return false;

  // Validate check digits
  let sum = 0;

  // First check digit
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }

  let remainder = sum % 11;
  if (remainder < 2) remainder = 0;
  else remainder = 11 - remainder;

  if (remainder !== parseInt(cpf.charAt(9))) return false;

  // Second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }

  remainder = sum % 11;
  if (remainder < 2) remainder = 0;
  else remainder = 11 - remainder;

  return remainder === parseInt(cpf.charAt(10));
};

/**
 * Formats date to Brazilian format
 */
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR');
};

/**
 * Formats datetime to Brazilian format
 */
export const formatDateTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('pt-BR');
};

/**
 * Data de hoje no fuso de Brasilia (America/Sao_Paulo) no formato YYYY-MM-DD.
 * Usar no lugar de new Date().toISOString().split('T')[0], que devolve a data
 * em UTC e adianta um dia a partir das 21h no horario local.
 */
export const dataHojeLocal = (): string => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};